/* GetFastShirt gang sheet builder (sections/gfs-gangsheet.liquid).
   Customers drop in art and set a size and number of copies for each design. Everything is packed onto a
   22.6 in wide DTF sheet (MaxRects, bottom-left rule, several sort orders, 90 degree turns only when they
   shorten the sheet), priced from the gang sheet product's length variants, and added to the cart with the
   files, the layout and a preview attached as line item properties. */
(function () {
  'use strict';
  const app = document.querySelector('[data-gsb]');
  if (!app || app.gsb) return;

  /* ---------- sheet geometry, in inches ---------- */
  const SHEET_W = 22.6, SIDE = 0.3, USABLE = 22, GAP = 0.25, END = 0.25, EPS = 1e-6;
  /* files: up to 20 MB each, like the rest of the site. Each sheet goes to the cart in one request with all of its
     files, so a sheet's files can add up to 40 MB. */
  const MIN_W = 0.5, MIN_H = 0.25, MAX_FILES = 60, MAX_MB = 20, WARN_MB = 10, SHEET_MB = 40, MB = 1048576, MAX_COPIES = 999, MAX_PIECES = 2000, MAX_SHEETS = 12, EDIT_MAX_PX = 20e6;

  /* ---------- packing (pure functions, no DOM) ---------- */
  const inside = (a, b) => a.x >= b.x - EPS && a.y >= b.y - EPS && a.x + a.w <= b.x + b.w + EPS && a.y + a.h <= b.y + b.h + EPS;
  /* lowest bottom edge wins, then the leftmost spot, then the unturned piece */
  function pick(b, x, y, w, h, rot) {
    const bot = y + h;
    if (!b || bot < b.bot - EPS || (bot < b.bot + EPS && (x < b.x - EPS || (x < b.x + EPS && b.rot && !rot)))) return { x: x, y: y, w: w, h: h, rot: rot, bot: bot };
    return b;
  }
  /* cut the placed piece out of every free rectangle it overlaps, then drop the pieces of free space that are
     contained in another one or too thin to hold anything */
  function split(free, r, minSide) {
    const add = [];
    for (let i = free.length - 1; i >= 0; i--) {
      const f = free[i];
      if (r.x >= f.x + f.w - EPS || r.x + r.w <= f.x + EPS || r.y >= f.y + f.h - EPS || r.y + r.h <= f.y + EPS) continue;
      free[i] = free[free.length - 1]; free.pop();
      if (r.x > f.x + EPS) add.push({ x: f.x, y: f.y, w: r.x - f.x, h: f.h });
      if (r.x + r.w < f.x + f.w - EPS) add.push({ x: r.x + r.w, y: f.y, w: f.x + f.w - r.x - r.w, h: f.h });
      if (r.y > f.y + EPS) add.push({ x: f.x, y: f.y, w: f.w, h: r.y - f.y });
      if (r.y + r.h < f.y + f.h - EPS) add.push({ x: f.x, y: r.y + r.h, w: f.w, h: f.y + f.h - r.y - r.h });
    }
    for (let i = 0; i < add.length; i++) {
      const a = add[i];
      if (a.w < minSide - EPS || a.h < minSide - EPS) continue;
      let dead = false;
      for (let j = 0; j < add.length && !dead; j++) if (j !== i && inside(a, add[j]) && (j < i || !inside(add[j], a))) dead = true;
      for (let j = 0; j < free.length && !dead; j++) if (inside(a, free[j])) dead = true;
      if (!dead) free.push(a);
    }
  }
  /* one sheet: pieces are art sizes; the gap is added to every piece and to the sheet width so neighbors never touch.
     Returns positions inside the printable area (the side and end margins are added later).
     Turning (a piece wider than the print area is always turned):
       0 never, 1 only to fill a gap without making the sheet longer, 2 whenever it sits lower,
       3 long side across the sheet, 4 long side down the sheet */
  function packBin(list, binW, binH, mode, gap) {
    const bw = binW + gap, free = [{ x: 0, y: 0, w: bw, h: binH + gap }], placed = [], left = [];
    let minSide = Infinity, cur = 0;
    for (const pc of list) minSide = Math.min(minSide, pc.w + gap, pc.h + gap);
    for (const pc of list) {
      const w = pc.w + gap, h = pc.h + gap;
      const canN = w <= bw + EPS, canR = h <= bw + EPS && Math.abs(w - h) > EPS;
      let tryN = canN, tryR = canR;
      if (mode === 0) tryR = canR && !canN;
      else if (mode === 3) { if (pc.h > pc.w + EPS && canR) tryN = false; else tryR = canR && !canN; }
      else if (mode === 4) { if (pc.w > pc.h + EPS && canR) tryN = false; else tryR = canR && !canN; }
      let bN = null, bR = null;
      for (let i = 0; i < free.length; i++) {
        const f = free[i];
        if (tryN && w <= f.w + EPS && h <= f.h + EPS) bN = pick(bN, f.x, f.y, w, h, false);
        if (tryR && h <= f.w + EPS && w <= f.h + EPS) bR = pick(bR, f.x, f.y, h, w, true);
      }
      let b;
      if (mode === 1) b = bN && bN.bot <= cur + EPS ? bN : bR && bR.bot <= cur + EPS ? bR : bN || bR;
      else b = !bN ? bR : !bR ? bN : pick(bN, bR.x, bR.y, bR.w, bR.h, true);
      if (!b) { left.push(pc); continue; }
      split(free, b, minSide);
      cur = Math.max(cur, b.bot);
      placed.push({ pc: pc, x: b.x, y: b.y, w: b.w - gap, h: b.h - gap, rot: b.rot });
    }
    return { placed: placed, left: left };
  }
  function packSheets(list, maxLen, mode) {
    const sheets = [];
    let rest = list;
    while (rest.length && sheets.length < MAX_SHEETS) {
      const r = packBin(rest, USABLE, maxLen, mode, GAP);
      if (!r.placed.length) break;
      let len = 0;
      for (const p of r.placed) len = Math.max(len, p.y + p.h);
      sheets.push({ placed: r.placed, len: len });
      rest = r.left;
    }
    return { sheets: sheets, rest: rest };
  }
  const ORDERS = [
    (a, b) => b.h - a.h || b.w - a.w,
    (a, b) => Math.max(b.w, b.h) - Math.max(a.w, a.h) || b.w * b.h - a.w * a.h,
    (a, b) => b.w * b.h - a.w * a.h || b.h - a.h,
    (a, b) => b.w - a.w || b.h - a.h,
    (a, b) => b.w + b.h - a.w - a.h || b.h - a.h
  ];
  /* smallest sheet that holds the art plus 0.25 in at each end */
  function fitVariant(variants, len) {
    const need = len + 2 * END - 0.001;
    for (const v of variants) if (v.len >= need) return v;
    return variants[variants.length - 1];
  }
  function better(a, b) {
    if (!b) return true;
    if (a.rest.length !== b.rest.length) return a.rest.length < b.rest.length;
    if (a.sheets.length !== b.sheets.length) return a.sheets.length < b.sheets.length;
    if (Math.abs(a.cost - b.cost) > EPS) return a.cost < b.cost;
    if (Math.abs(a.total - b.total) > 0.01) return a.total < b.total;
    return a.rots < b.rots;
  }
  /* try several orders, first without turning anything, then letting pieces turn; keep the shortest result
     (turned layouts only win when they are actually shorter or cheaper) */
  function arrange(pcs, variants) {
    const maxLen = variants[variants.length - 1].len - 2 * END;
    const n = pcs.length, orders = n > 1200 ? 1 : n > 400 ? 2 : ORDERS.length;
    const turnable = pcs.some(p => Math.abs(p.w - p.h) > EPS);
    const modes = !turnable ? [0] : n > 1200 ? [0, 1] : n > 400 ? [0, 1, 2] : [0, 1, 2, 3, 4];
    let best = null;
    for (const mode of modes) {
      for (let o = 0; o < orders; o++) {
        const r = packSheets(pcs.slice().sort(ORDERS[o]), maxLen, mode);
        r.cost = 0; r.total = 0; r.rots = 0;
        for (const s of r.sheets) {
          s.variant = fitVariant(variants, s.len);
          r.cost += s.variant.price != null ? s.variant.price : s.variant.len;
          r.total += s.len;
          for (const p of s.placed) if (p.rot) r.rots++;
        }
        if (better(r, best)) best = r;
      }
    }
    return best;
  }
  /* ---------- end packing ---------- */

  const $ = (s, el) => (el || app).querySelector(s);
  let CFG = {};
  try { CFG = JSON.parse($('[data-gsb-config]').textContent) || {}; } catch (e) { CFG = {}; }
  const ROOT = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  /* DTF or UV DTF: both sheets share lengths and layout rules; only the product (prices, 48-hour UV turnaround) differs */
  const DTF_HANDLE = CFG.handle || 'dtf-gang-sheets-by-length-22-6-in-wide';
  let TYPE = (CFG.uvOn && CFG.uvHandle && new URLSearchParams(location.search).get('type') === 'uv') ? 'uv' : 'dtf';
  CFG.handle = TYPE === 'uv' ? CFG.uvHandle : DTF_HANDLE;
  const PHONE = CFG.phone || '888-995-6578', TEL = 'tel:+1' + PHONE.replace(/\D/g, '');
  const QUOTE = CFG.quote || '/pages/request-a-quote';
  const CUR = (window.Shopify && Shopify.currency && Shopify.currency.active) || CFG.currency || 'USD';
  let NF = null;
  try { NF = new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency: CUR }); } catch (e) { NF = null; }
  const money = c => (NF ? NF.format(c / 100) : '$' + (c / 100).toFixed(2));
  const esc = s => String(s == null ? '' : s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const r2 = x => Math.round(x * 100) / 100;
  const fin = x => String(r2(x));
  const f1 = x => (Math.round(x * 10) / 10).toFixed(1).replace(/\.0$/, '');
  const tick = () => new Promise(r => setTimeout(r, 30));
  const nice = t => String(t).replace(/(\d)\s*x\s*(\d)/i, '$1 × $2');
  const shortName = v => { const m = /\(([^)]+)\)/.exec(v.title); return m ? m[1] : fin(v.len) + ' in'; };
  const EMAIL = CFG.email || 'support@getfastshirt.com', MAILTO = '<a href="mailto:' + esc(EMAIL) + '">' + esc(EMAIL) + '</a>';
  /* limits shown to customers: round the minimum up and the maximum down, so the numbers shown are always allowed */
  const up2 = x => String(Math.ceil(x * 100 - 1e-6) / 100), dn2 = x => String(Math.floor(x * 100 + 1e-6) / 100);

  /* ---------- state ---------- */
  /* inCart: sheets this builder has put in the cart, as { hash, key, title } (see the cart section) */
  const S = { items: [], seq: 0, prod: 'loading', variants: [], pack: null, adding: false, trash: null, inCart: [] };
  function fallbackVariants() {
    const ls = String(CFG.lengths || '24,36,60,84,120,240').split(',').map(parseFloat).filter(x => x > 1).sort((a, b) => a - b);
    return (ls.length ? ls : [24, 36, 60, 84, 120, 240]).map(l => ({ id: null, len: l, price: null, title: SHEET_W + ' x ' + fin(l) + ' in (' + fin(l / 12) + ' ft)' }));
  }
  S.variants = fallbackVariants();
  const maxArtLen = () => S.variants[S.variants.length - 1].len - 2 * END;

  const el = {
    file: $('[data-gsb-file]'), drop: $('[data-gsb-drop]'), list: $('[data-gsb-items]'), msg: $('[data-gsb-msg]'),
    head: $('[data-gsb-listhead]'), soon: $('[data-gsb-soon]'), mini: $('[data-gsb-mini]'),
    title: $('[data-gsb-title]'), price: $('[data-gsb-price]'), sub: $('[data-gsb-sub]'), prev: $('[data-gsb-prev]'),
    add: $('[data-gsb-add]'), note: $('[data-gsb-note]'), prog: $('[data-gsb-prog]'), fine: $('[data-gsb-fine]'), work: $('[data-gsb-work]')
  };

  /* ---------- product ---------- */
  /* sheet length in inches from a variant like "22.6 x 24 in (2 ft)", "2 ft" or "24 in" */
  function parseLen(v) {
    const t = [v.option1, v.title, v.public_title].filter(Boolean).join(' ');
    let m = /[x×]\s*(\d+(?:\.\d+)?)\s*(?:in\b|inch|")/i.exec(t);
    if (m) return parseFloat(m[1]);
    m = /(\d+(?:\.\d+)?)\s*(?:ft\b|feet|foot)/i.exec(t);
    if (m) return parseFloat(m[1]) * 12;
    let best = 0;
    const re = /(\d+(?:\.\d+)?)\s*(?:in\b|inch|")/gi;
    while ((m = re.exec(t))) best = Math.max(best, parseFloat(m[1]));
    return best;
  }
  async function loadProduct() {
    try {
      const r = await fetch(ROOT + 'products/' + encodeURIComponent(CFG.handle || 'dtf-gang-sheets-by-length-22-6-in-wide') + '.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
      if (r.status === 404) S.prod = 'missing';
      else if (!r.ok) S.prod = 'error';
      else {
        const p = await r.json();
        const vs = (p.variants || []).map(v => ({ id: v.id, title: v.title, len: parseLen(v), price: v.price, available: v.available !== false }))
          .filter(v => v.len > 2 * END && v.id).sort((a, b) => a.len - b.len || a.price - b.price);
        const ok = vs.filter(v => v.available);
        if (ok.length) { S.variants = ok; S.prod = 'ok'; }
        else S.prod = vs.length ? 'soldout' : 'error';
      }
    } catch (e) { S.prod = 'error'; }
    S.items.forEach(it => { if (it.w) it.w = clampW(it, it.w); });
    renderStatus(); renderList(); schedule();
  }
  function renderStatus() {
    const s = S.prod;
    let h = '';
    if (s === 'missing') h = '<b>Gang sheets are coming online shortly.</b><span>You can build your layout now to see the sheet size. To order today, call us or request a quote.</span>';
    else if (s === 'soldout') h = '<b>Gang sheets are unavailable right now.</b><span>You can still build your layout here. To order, call us or request a quote.</span>';
    else if (s === 'error') h = '<b>We couldn’t load gang sheet prices.</b><span>Refresh the page to try again. You can still build your layout, or call us to order.</span>';
    el.soon.hidden = !h;
    if (h) el.soon.innerHTML = h + '<div class="gsb-row"><a class="btn btn-violet" href="' + TEL + '">Call ' + esc(PHONE) + '</a><a class="btn btn-line" href="' + esc(QUOTE) + '">Request a quote</a></div>';
    renderFoot();
  }

  /* ---------- files ---------- */
  const isImg = f => /^image\/(png|jpeg|webp)$/i.test(f.type || '') || /\.(png|jpe?g|webp)$/i.test(f.name || '');
  const loadImg = url => new Promise((res, rej) => { const im = new Image(); im.onload = () => res(im); im.onerror = () => rej(new Error('decode')); im.src = url; });
  /* print size saved in the file (PNG pHYs or JPEG JFIF), in dots per inch; 0 when there is none */
  async function fileDpi(f) {
    try {
      const b = new Uint8Array(await f.slice(0, 262144).arrayBuffer()), dv = new DataView(b.buffer);
      if (b[0] === 0x89 && b[1] === 0x50 && b[2] === 0x4e && b[3] === 0x47) {
        let o = 8;
        while (o + 12 <= b.length) {
          const len = dv.getUint32(o), type = String.fromCharCode(b[o + 4], b[o + 5], b[o + 6], b[o + 7]);
          if (type === 'pHYs' && o + 17 <= b.length) return b[o + 16] === 1 ? dv.getUint32(o + 8) * 0.0254 : 0;
          if (type === 'IDAT' || type === 'IEND') break;
          o += 12 + len;
        }
      } else if (b[0] === 0xff && b[1] === 0xd8 && b[2] === 0xff && b[3] === 0xe0 && String.fromCharCode(b[6], b[7], b[8], b[9]) === 'JFIF') {
        const unit = b[13], d = dv.getUint16(14);
        return unit === 1 ? d : unit === 2 ? d * 2.54 : 0;
      }
    } catch (e) { /* no size saved */ }
    return 0;
  }
  const ratio = it => it.pxH / it.pxW;
  const heightOf = it => it.w * ratio(it);
  /* the short side has to fit across the sheet and the long side along the longest sheet */
  function limits(it) {
    const a = ratio(it), L = maxArtLen();
    const max = Math.floor((a >= 1 ? Math.min(USABLE, L / a) : Math.min(L, USABLE / a)) * 100) / 100;
    const min = Math.ceil(Math.max(MIN_W, MIN_H / a) * 100) / 100;
    return { min: Math.min(min, max), max: max };
  }
  function clampW(it, w) { const l = limits(it); return Math.min(l.max, Math.max(l.min, r2(w))); }
  function makeSources(it, img) {
    const W = img.naturalWidth || img.width, H = img.naturalHeight || img.height;
    const k = Math.min(1, 1024 / Math.max(W, H));
    const c = document.createElement('canvas');
    c.width = Math.max(1, Math.round(W * k)); c.height = Math.max(1, Math.round(H * k));
    const x = c.getContext('2d');
    x.imageSmoothingQuality = 'high';
    x.drawImage(img, 0, 0, c.width, c.height);
    it.src = c;
    const kt = Math.min(1, 160 / Math.max(c.width, c.height)), t = document.createElement('canvas');
    t.width = Math.max(1, Math.round(c.width * kt)); t.height = Math.max(1, Math.round(c.height * kt));
    const tx = t.getContext('2d');
    tx.imageSmoothingQuality = 'high';
    tx.drawImage(c, 0, 0, t.width, t.height);
    it.thumb = t.toDataURL('image/png');
  }
  /* does it have see-through pixels, and is its border a solid near-white box? */
  function scanEdges(it) {
    const c = it.src, W = c.width, H = c.height, d = c.getContext('2d').getImageData(0, 0, W, H).data;
    let n = 0, opaque = 0, white = 0, clear = false;
    const at = i => { const o = i * 4; n++; if (d[o + 3] > 200) { opaque++; if (Math.min(d[o], d[o + 1], d[o + 2]) >= 225) white++; } };
    for (let x = 0; x < W; x++) { at(x); if (H > 1) at((H - 1) * W + x); }
    for (let y = 1; y < H - 1; y++) { at(y * W); if (W > 1) at(y * W + W - 1); }
    for (let o = 3; o < d.length; o += 8) if (d[o] < 250) { clear = true; break; }
    it.clear = clear;
    it.whiteEdge = n > 0 && white / n > 0.6;
  }
  async function readItem(it) {
    const url = URL.createObjectURL(it.file);
    try {
      const img = await loadImg(url);
      it.pxW = img.naturalWidth; it.pxH = img.naturalHeight;
      if (!it.pxW || !it.pxH) throw new Error('empty');
      makeSources(it, img);
      scanEdges(it);
      const dpi = await fileDpi(it.file);
      const w = dpi >= 100 && dpi <= 1200 ? it.pxW / dpi : Math.min(10, Math.max(2, it.pxW / 300));
      it.w = clampW(it, w);
    } finally { URL.revokeObjectURL(url); }
  }
  async function addFiles(fileList) {
    const files = Array.from(fileList || []);
    if (!files.length || S.adding) return;
    const skipped = [], fresh = [];
    for (const f of files) {
      const nm = '<b>' + esc(f.name || 'That file') + '</b>';
      if (S.items.length + fresh.length >= MAX_FILES) { skipped.push(nm + ' wasn’t added. A sheet holds up to ' + MAX_FILES + ' designs.'); continue; }
      if (!isImg(f)) { skipped.push(nm + ' isn’t a PNG, JPG or WebP. Save it as a PNG with a transparent background.'); continue; }
      if (f.size > MAX_MB * MB) { skipped.push(nm + ' is over ' + MAX_MB + ' MB. Email it to ' + MAILTO + ' and we’ll add it to your sheet.'); continue; }
      fresh.push({ id: ++S.seq, name: f.name || 'image.png', file: f, work: f, copies: 1, w: 0, loading: true });
    }
    S.items.push.apply(S.items, fresh);
    if (skipped.length) say(skipped.join('<br>'), 'warn');
    renderList(); schedule();
    let ok = 0;
    for (const it of fresh) {
      try { await readItem(it); it.loading = false; ok++; }
      catch (e) {
        S.items = S.items.filter(x => x !== it);
        skipped.push('<b>' + esc(it.name) + '</b> couldn’t be opened. Try saving it again as a PNG.');
      }
      renderList(); schedule();
    }
    const added = !ok ? '' : ok === 1 ? 'Added 1 design.' : 'Added ' + ok + ' designs.';
    if (skipped.length) say((added ? added + '<br>' : '') + skipped.join('<br>'), 'warn');
    else if (ok) say(added + ' Set the width and copies for each one.');
  }

  /* ---------- remove a white background: flood fill from the edges, then soften the one pixel rim ---------- */
  function knockOut(p, W, H) {
    const n = W * H;
    let sr = 0, sg = 0, sb = 0, c = 0;
    const edge = i => { const o = i * 4; if (p[o + 3] > 200 && Math.min(p[o], p[o + 1], p[o + 2]) >= 215) { sr += p[o]; sg += p[o + 1]; sb += p[o + 2]; c++; } };
    for (let x = 0; x < W; x++) { edge(x); edge(n - W + x); }
    for (let y = 1; y < H - 1; y++) { edge(y * W); edge(y * W + W - 1); }
    if (!c) return false;
    const R = sr / c, G = sg / c, B = sb / c, TC = 28, TS = 120;
    const dist = o => Math.max(Math.abs(p[o] - R), Math.abs(p[o + 1] - G), Math.abs(p[o + 2] - B));
    const mark = new Uint8Array(n), q = new Int32Array(n);
    let qt = 0;
    const grow = i => { if (!mark[i]) { const o = i * 4; if (p[o + 3] < 16 || dist(o) <= TC) { mark[i] = 1; q[qt++] = i; } } };
    for (let x = 0; x < W; x++) { grow(x); grow(n - W + x); }
    for (let y = 1; y < H - 1; y++) { grow(y * W); grow(y * W + W - 1); }
    for (let qh = 0; qh < qt; qh++) {
      const i = q[qh], x = i % W;
      if (x > 0) grow(i - 1);
      if (x < W - 1) grow(i + 1);
      if (i >= W) grow(i - W);
      if (i < n - W) grow(i + W);
    }
    if (!qt) return false;
    for (let k = 0; k < qt; k++) p[q[k] * 4 + 3] = 0;
    const rim = j => {
      if (mark[j]) return;
      mark[j] = 2;
      const o = j * 4, d = dist(o);
      if (d >= TS) return;
      const a = Math.max(0.08, (d - TC) / (TS - TC));
      p[o] = (p[o] - (1 - a) * R) / a; p[o + 1] = (p[o + 1] - (1 - a) * G) / a; p[o + 2] = (p[o + 2] - (1 - a) * B) / a;
      p[o + 3] = Math.round(p[o + 3] * a);
    };
    for (let k = 0; k < qt; k++) {
      const i = q[k], x = i % W;
      if (x > 0) rim(i - 1);
      if (x < W - 1) rim(i + 1);
      if (i >= W) rim(i - W);
      if (i < n - W) rim(i + W);
    }
    return true;
  }
  async function removeWhite(it) {
    if (it.busy || S.adding) return;
    if (it.pxW * it.pxH > EDIT_MAX_PX) return say(esc(it.name) + ' is too large to edit in the browser. Upload a PNG with a transparent background instead.', 'warn');
    it.busy = true; renderList();
    await tick();
    const url = URL.createObjectURL(it.file);
    try {
      const img = await loadImg(url);
      const c = document.createElement('canvas');
      c.width = it.pxW; c.height = it.pxH;
      const x = c.getContext('2d', { willReadFrequently: true });
      x.drawImage(img, 0, 0);
      const data = x.getImageData(0, 0, c.width, c.height);
      if (!knockOut(data.data, c.width, c.height)) throw new Error('none');
      x.putImageData(data, 0, 0);
      const blob = await new Promise(res => c.toBlob(res, 'image/png'));
      if (!blob) throw new Error('encode');
      if (blob.size > MAX_MB * MB) throw new Error('big');
      const base = it.name.replace(/\.[a-z0-9]+$/i, '');
      it.work = new File([blob], base + '-no-white.png', { type: 'image/png' });
      it.knocked = true;
      makeSources(it, c);
      scanEdges(it);
      c.width = c.height = 1;
      say('White background removed from ' + esc(it.name) + '. Check the preview; undo if it took too much.');
    } catch (e) {
      const why = e && e.message;
      say(why === 'none' ? 'We couldn’t find a white background around ' + esc(it.name) + '.' :
        why === 'big' ? 'Without its white background, ' + esc(it.name) + ' would be over ' + MAX_MB + ' MB, so we left it as it was. Email the file to ' + MAILTO + ' and we’ll take care of it.' :
        'That didn’t work on ' + esc(it.name) + '. Upload a PNG with a transparent background instead.', 'warn');
    } finally { URL.revokeObjectURL(url); it.busy = false; }
    renderList(); schedule();
  }
  async function undoWhite(it) {
    if (it.busy || S.adding) return;
    it.busy = true; renderList();
    const url = URL.createObjectURL(it.file);
    try { makeSources(it, await loadImg(url)); scanEdges(it); it.work = it.file; it.knocked = false; }
    catch (e) { say('Couldn’t undo that. Remove the design and add it again.', 'warn'); }
    finally { URL.revokeObjectURL(url); it.busy = false; }
    renderList(); schedule();
  }

  /* ---------- messages ---------- */
  function say(html, kind, undo) {
    el.msg.className = 'gsb-msg' + (kind ? ' is-' + kind : '');
    el.msg.innerHTML = html + (undo ? ' <button type="button" class="gsb-link" data-gsb-undo>Undo</button>' : '');
    el.msg.hidden = !html;
  }

  /* ---------- item list ---------- */
  function quality(it) {
    const dpi = Math.round(it.pxW / it.w);
    if (dpi >= 200) return { c: 'good', t: 'Great quality', d: dpi + ' DPI at this size.' };
    if (dpi >= 120) return { c: 'ok', t: 'Good quality', d: dpi + ' DPI at this size. For the sharpest print, keep it ' + f1(it.pxW / 200) + ' in wide or less.' };
    return { c: 'low', t: 'Low resolution', d: 'Only ' + dpi + ' DPI at this size, so it may print blurry. Use a larger file or make it ' + f1(Math.max(it.pxW / 120, 0.1)) + ' in wide or less.' };
  }
  function tools(it) {
    const out = [];
    if (it.busy) out.push('<p class="gsb-tool"><span class="gsb-spin" aria-hidden="true"></span><span>Working on it…</span></p>');
    else if (it.knocked) out.push('<p class="gsb-tool is-done"><span>White background removed.</span><button type="button" class="gsb-link" data-k="undo">Undo</button></p>');
    else if (it.whiteEdge && !it.clear) out.push('<p class="gsb-tool"><span>This has a white background, so it prints as a white box.</span><button type="button" class="gsb-chip" data-k="knock">Remove white background</button></p>');
    else if (!it.clear) out.push('<p class="gsb-tool"><span>No transparent background, so the whole rectangle prints.</span></p>');
    if (heightOf(it) <= USABLE + EPS && it.w > USABLE + EPS) out.push('<p class="gsb-tool"><span>Wider than the sheet, so it runs lengthwise.</span></p>');
    if (it.work.size > WARN_MB * 1048576) out.push('<p class="gsb-tool is-warn"><span>Large file (' + Math.round(it.work.size / 1048576) + ' MB). Uploading may take a while.</span></p>');
    return out.join('');
  }
  function rowHTML(it, idx) {
    if (it.loading) return '<li class="gsb-item is-loading" data-id="' + it.id + '"><div class="gsb-thumb"><span class="gsb-spin" aria-hidden="true"></span></div><div class="gsb-info"><p class="gsb-name"><b>' + esc(it.name) + '</b><small>Opening…</small></p></div></li>';
    const q = quality(it), l = limits(it), nm = esc(it.name), eid = 'gsb-err-' + it.id;
    const bad = k => (it.err && it.errK === k ? ' is-bad' : ''), aria = k => ' aria-describedby="' + eid + '"' + (it.err && it.errK === k ? ' aria-invalid="true"' : '');
    return '<li class="gsb-item" data-id="' + it.id + '">' +
      '<div class="gsb-thumb"><img src="' + it.thumb + '" alt="" width="84" height="84"><span class="gsb-n">' + (idx + 1) + '</span></div>' +
      '<div class="gsb-info">' +
        '<p class="gsb-name"><b title="' + nm + '">Image ' + (idx + 1) + ' · ' + nm + '</b><small>' + it.pxW + ' × ' + it.pxH + ' px</small></p>' +
        '<div class="gsb-ctrls">' +
          '<label class="gsb-f"><span>Width</span><span class="gsb-in' + bad('w') + '"><input type="number" inputmode="decimal" step="0.1" min="' + l.min + '" max="' + l.max + '" value="' + fin(it.w) + '" data-k="w"' + aria('w') + ' aria-label="Width of image ' + (idx + 1) + ' in inches"><em>in</em></span></label>' +
          '<label class="gsb-f"><span>Height</span><span class="gsb-in' + bad('h') + '"><input type="number" inputmode="decimal" step="0.1" value="' + fin(heightOf(it)) + '" data-k="h"' + aria('h') + ' aria-label="Height of image ' + (idx + 1) + ' in inches"><em>in</em></span></label>' +
          '<div class="gsb-f"><span>Copies</span><span class="gsb-step' + bad('copies') + '"><button type="button" data-k="dec" aria-label="One fewer copy of image ' + (idx + 1) + '">−</button><input type="number" inputmode="numeric" min="1" max="' + MAX_COPIES + '" step="1" value="' + it.copies + '" data-k="copies"' + aria('copies') + ' aria-label="Copies of image ' + (idx + 1) + '"><button type="button" data-k="inc" aria-label="One more copy of image ' + (idx + 1) + '">+</button></span></div>' +
        '</div>' +
        '<p class="gsb-err" id="' + eid + '" data-err aria-live="polite"' + (it.err ? '' : ' hidden') + '>' + esc(it.err || '') + '</p>' +
        '<p class="gsb-q is-' + q.c + '" data-q><b>' + q.t + '</b> <span>' + q.d + '</span></p>' +
        '<div class="gsb-tools" data-tools>' + tools(it) + '</div>' +
      '</div>' +
      '<button type="button" class="gsb-x" data-k="rm" aria-label="Remove image ' + (idx + 1) + ', ' + nm + '"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"/></svg></button>' +
    '</li>';
  }
  function renderList() {
    el.list.innerHTML = S.items.map(rowHTML).join('');
    el.head.hidden = !S.items.length;
  }
  const byId = id => S.items.find(it => String(it.id) === String(id));
  function refreshRow(it, force) {
    const li = el.list.querySelector('.gsb-item[data-id="' + it.id + '"]');
    if (!li || it.loading) return;
    const wi = li.querySelector('[data-k="w"]'), hi = li.querySelector('[data-k="h"]'), ci = li.querySelector('[data-k="copies"]');
    if (force || document.activeElement !== wi) wi.value = fin(it.w);
    if (force || document.activeElement !== hi) hi.value = fin(heightOf(it));
    if (force || document.activeElement !== ci) ci.value = it.copies;
    const q = quality(it), qe = li.querySelector('[data-q]');
    qe.className = 'gsb-q is-' + q.c;
    qe.innerHTML = '<b>' + q.t + '</b> <span>' + q.d + '</span>';
    const te = li.querySelector('[data-tools]'), th = tools(it);
    if (te.innerHTML !== th) te.innerHTML = th;
  }
  /* one short message per design, under its fields. Any valid entry on that design clears it. */
  function fieldMsg(it, k, text) {
    it.err = text || ''; it.errK = text ? k : '';
    const li = el.list.querySelector('.gsb-item[data-id="' + it.id + '"]');
    if (!li || it.loading) return;
    const m = li.querySelector('[data-err]');
    if (m) { m.textContent = it.err; m.hidden = !it.err; }
    li.querySelectorAll('input[data-k]').forEach(inp => {
      const on = !!it.err && inp.dataset.k === it.errK, box = inp.closest('.gsb-in,.gsb-step');
      if (on) inp.setAttribute('aria-invalid', 'true'); else inp.removeAttribute('aria-invalid');
      if (box) box.classList.toggle('is-bad', on);
    });
  }
  function rangeText(it, k) {
    const l = limits(it), a = k === 'h' ? ratio(it) : 1;
    return 'Enter a ' + (k === 'h' ? 'height' : 'width') + ' between ' + up2(l.min * a) + ' and ' + dn2(l.max * a) + ' in.';
  }
  const sizeOK = (it, want) => { const l = limits(it); return want >= l.min - 0.005 && want <= l.max + 0.005; };
  const COPIES_MAX = 'Up to ' + MAX_COPIES + ' copies per design. For more, call ' + PHONE + ' or request a quote.';
  function setCopies(it, n) {
    const want = Math.floor(n) || 1;
    it.copies = Math.max(1, Math.min(MAX_COPIES, want));
    fieldMsg(it, 'copies', want > MAX_COPIES ? COPIES_MAX : '');
    refreshRow(it, true); schedule();
  }
  function removeItems(list, label) {
    S.trash = { items: S.items.slice() };
    S.items = S.items.filter(it => list.indexOf(it) < 0);
    renderList(); schedule();
    say(label, '', true);
  }

  /* while typing: use every usable number right away. Half-typed entries ("", "0.", "-") wait for the change event. */
  el.list.addEventListener('input', e => {
    const inp = e.target, li = inp.closest('.gsb-item'), it = li && byId(li.dataset.id);
    if (!it || it.loading || S.adding) return;
    const k = inp.dataset.k, v = parseFloat(inp.value);
    if (k !== 'w' && k !== 'h' && k !== 'copies') return;
    if (!(v > 0)) return;
    if (k === 'copies') {
      if (v < 1) return;
      it.copies = Math.min(MAX_COPIES, Math.floor(v));
      fieldMsg(it, k, v >= MAX_COPIES + 1 ? COPIES_MAX : '');
    } else {
      const want = k === 'w' ? v : v / ratio(it);
      it.w = clampW(it, want);
      fieldMsg(it, k, sizeOK(it, want) ? '' : rangeText(it, k));
    }
    refreshRow(it); schedule();
  });
  /* when the entry is done: explain anything we couldn't use or had to change, then show the value in use */
  el.list.addEventListener('change', e => {
    const inp = e.target, li = inp.closest('.gsb-item'), it = li && byId(li.dataset.id);
    if (!it || it.loading) return;
    const k = inp.dataset.k, v = parseFloat(inp.value);
    if (k !== 'w' && k !== 'h' && k !== 'copies') return;
    if (!S.adding) {
      let moved = false;
      if (k === 'copies') {
        if (v >= 1) { const c = Math.min(MAX_COPIES, Math.floor(v)); moved = c !== it.copies; it.copies = c; }
        fieldMsg(it, k, !(v >= 1) ? 'Enter 1 to ' + MAX_COPIES + ' copies.' : v >= MAX_COPIES + 1 ? COPIES_MAX : '');
      } else {
        const want = v > 0 ? (k === 'w' ? v : v / ratio(it)) : NaN;
        if (want > 0) { const w = clampW(it, want); moved = w !== it.w; it.w = w; }
        fieldMsg(it, k, !(want > 0) ? rangeText(it, k) : !sizeOK(it, want) ? rangeText(it, k) + ' We set it to ' + fin(k === 'w' ? it.w : heightOf(it)) + ' in.' : '');
      }
      if (moved) schedule();
    }
    refreshRow(it, true);
  });
  el.list.addEventListener('click', e => {
    const b = e.target.closest('button[data-k]'), li = b && b.closest('.gsb-item'), it = li && byId(li.dataset.id);
    if (!it || S.adding) return;
    const k = b.dataset.k;
    if (k === 'inc') setCopies(it, it.copies + 1);
    else if (k === 'dec') setCopies(it, it.copies - 1);
    else if (k === 'rm') removeItems([it], 'Removed ' + esc(it.name) + '.');
    else if (k === 'knock') removeWhite(it);
    else if (k === 'undo') undoWhite(it);
  });
  el.msg.addEventListener('click', e => {
    if (!e.target.closest('[data-gsb-undo]') || !S.trash || S.adding) return;
    const before = S.trash.items;
    S.items = before.concat(S.items.filter(it => before.indexOf(it) < 0));
    S.trash = null;
    renderList(); schedule(); say('Put back.');
  });
  const clearBtn = $('[data-gsb-clear]');
  if (clearBtn) clearBtn.addEventListener('click', () => { if (!S.adding && S.items.length) removeItems(S.items.slice(), 'Removed all designs.'); });

  el.file.addEventListener('change', () => { const fs = Array.from(el.file.files || []); el.file.value = ''; addFiles(fs); });
  const hasFiles = e => e.dataTransfer && Array.from(e.dataTransfer.types || []).indexOf('Files') >= 0;
  const zone = $('[data-gsb-zone]') || app;
  let dragDepth = 0;
  zone.addEventListener('dragenter', e => { if (!hasFiles(e)) return; e.preventDefault(); dragDepth++; el.drop.classList.add('is-over'); });
  zone.addEventListener('dragover', e => { if (!hasFiles(e)) return; e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; });
  zone.addEventListener('dragleave', e => { if (!hasFiles(e)) return; dragDepth = Math.max(0, dragDepth - 1); if (!dragDepth) el.drop.classList.remove('is-over'); });
  zone.addEventListener('drop', e => { if (!hasFiles(e)) return; e.preventDefault(); dragDepth = 0; el.drop.classList.remove('is-over'); addFiles(e.dataTransfer.files); });
  document.addEventListener('paste', e => {
    const fs = Array.from((e.clipboardData && e.clipboardData.files) || []).filter(isImg);
    if (fs.length) { e.preventDefault(); addFiles(fs); }
  });

  /* ---------- arrange and price ---------- */
  let packTimer = 0;
  function schedule() { clearTimeout(packTimer); packTimer = setTimeout(() => { packTimer = 0; run(); }, 50); }
  function flush() { if (packTimer) { clearTimeout(packTimer); packTimer = 0; run(); } }
  function run() {
    const pcs = [];
    S.items.forEach((it, idx) => {
      if (it.loading || !it.w) return;
      const w = it.w, h = heightOf(it);
      for (let k = 0; k < it.copies; k++) pcs.push({ it: it, n: idx + 1, w: w, h: h });
    });
    if (pcs.length > MAX_PIECES) S.pack = { tooMany: pcs.length, sheets: [], rest: [], count: 0 };
    else if (!pcs.length) S.pack = null;
    else {
      const r = arrange(pcs, S.variants);
      r.count = pcs.length - r.rest.length;
      r.designs = S.items.filter(it => !it.loading && it.w).length;
      r.sheets.forEach(sh => { sh.bytes = usedOf(sh).reduce((t, u) => t + u.it.work.size, 0); });
      S.pack = r;
    }
    renderSummary();
    drawAll();
  }

  function renderSummary() {
    const P = S.pack, live = S.prod === 'ok';
    let title = 'Your sheet', price = '', sub = 'Add your art to see the layout and price.';
    if (S.items.some(it => it.loading)) sub = 'Opening your art…';
    if (P && P.tooMany) sub = 'That’s more than ' + MAX_PIECES.toLocaleString() + ' pieces. For a run that big, call us or request a quote.';
    else if (P && P.sheets.length) {
      const n = P.sheets.length;
      title = n === 1 ? nice(P.sheets[0].variant.title) : n + ' sheets';
      price = live ? money(P.cost) : '';
      sub = P.count + (P.count === 1 ? ' piece' : ' pieces') + ' from ' + P.designs + (P.designs === 1 ? ' design' : ' designs') + '. ' +
        (n === 1 ? 'Art runs ' + f1(P.sheets[0].len) + ' in down the sheet.' : 'Your art is longer than our longest sheet, so it’s split across ' + n + '.');
      if (P.rest.length) sub += ' ' + P.rest.length + ' pieces don’t fit on ' + MAX_SHEETS + ' sheets. Remove some copies, or call us.';
    }
    el.title.textContent = title;
    el.price.textContent = price;
    el.sub.textContent = sub;
    if (el.mini) {
      el.mini.hidden = !(P && P.sheets.length);
      if (P && P.sheets.length) {
        const n = P.sheets.length;
        el.mini.innerHTML = '<span>' + esc(n === 1 ? shortName(P.sheets[0].variant) + ' sheet' : n + ' sheets') + (live ? ' · <b>' + money(P.cost) + '</b>' : '') + '</span><span>See layout ↓</span>';
      }
    }
    renderFoot();
  }
  let noteHTML = '';
  function setNote(h) { if (h !== noteHTML) { noteHTML = h; el.note.innerHTML = h; } el.note.hidden = !h; }
  /* the first sheet whose files are over the per-sheet upload limit, or -1 */
  const heavyAt = P => (P && P.sheets ? P.sheets.findIndex(sh => sh.bytes > SHEET_MB * MB) : -1);
  function renderFoot() {
    if (S.adding) return;
    const P = S.pack, live = S.prod === 'ok', hv = heavyAt(P);
    const ready = P && P.sheets.length && !P.rest.length && !P.tooMany && hv < 0 && !S.items.some(it => it.loading || it.busy);
    el.add.hidden = !(live || S.prod === 'loading');
    el.add.disabled = !(live && ready);
    const n = P && P.sheets.length ? P.sheets.length : 0;
    let label = !live ? 'Loading prices…' : !n ? 'Add to cart' : (n === 1 ? 'Add to cart' : 'Add ' + n + ' sheets to cart') + ' · ' + money(P.cost);
    if (live && n && S.inCart.length) {
      const pl = plan(P);
      if (!pl.stale.length && !pl.todo.length) label = 'In your cart · View cart';
      else if (!pl.stale.length && pl.todo.length < n) label = (pl.todo.length === 1 ? 'Add the remaining sheet' : 'Add the remaining ' + pl.todo.length + ' sheets') + ' · ' + money(pl.cost);
    }
    el.add.textContent = label;
    let note = '';
    if (hv >= 0) note += '<p class="gsb-warn">' + (n > 1 ? 'Sheet ' + (hv + 1) + '’s files add up to ' : 'Your files add up to ') + f1(Math.max(SHEET_MB + 0.1, P.sheets[hv].bytes / MB)) + ' MB. We can take up to ' + SHEET_MB + ' MB per sheet online. Use smaller files, or email them to ' + MAILTO + ' and we’ll build your sheet.</p>';
    if (!live && S.prod !== 'loading') note += '<p>' + (S.prod === 'missing' ? 'Online ordering opens when gang sheets go live.' : S.prod === 'soldout' ? 'Gang sheets can’t be ordered online right now.' : 'Prices didn’t load, so this is a preview.') + ' To order now, call or request a quote.</p><div class="gsb-row"><a class="btn btn-violet" href="' + TEL + '">Call ' + esc(PHONE) + '</a><a class="btn btn-line" href="' + esc(QUOTE) + '">Request a quote</a></div>';
    if (TYPE === 'uv' && live) note += '<p><b>UV DTF</b>: peel and stick on hard surfaces, ready in 48 hours after your art is approved.</p>';
    setNote(note);
    el.fine.hidden = !live;
  }

  /* ---------- drawing ---------- */
  let checker = null;
  function checkerTile() {
    if (checker) return checker;
    checker = document.createElement('canvas');
    checker.width = checker.height = 16;
    const x = checker.getContext('2d');
    x.fillStyle = '#FFFFFF'; x.fillRect(0, 0, 16, 16);
    x.fillStyle = '#ECEAF1'; x.fillRect(0, 0, 8, 8); x.fillRect(8, 8, 8, 8);
    return checker;
  }
  function drawArt(x, sh, k, X0, Y0, outline) {
    x.imageSmoothingQuality = 'high';
    for (const p of sh.placed) {
      const it = p.pc.it, ax = X0 + (SIDE + p.x) * k, ay = Y0 + (END + p.y) * k, fw = p.w * k, fh = p.h * k;
      if (p.rot) { x.save(); x.translate(ax + fw, ay); x.rotate(Math.PI / 2); x.drawImage(it.src, 0, 0, fh, fw); x.restore(); }
      else x.drawImage(it.src, ax, ay, fw, fh);
      if (outline) { x.strokeStyle = 'rgba(128,44,201,.3)'; x.lineWidth = 1; x.strokeRect(ax + 0.5, ay + 0.5, Math.max(0, fw - 1), Math.max(0, fh - 1)); }
    }
  }
  function drawSheet(cv, sh, len, cssW) {
    const G = 34, top = 8, k = Math.max(1, (cssW - G - 6) / SHEET_W), cssH = Math.round(len * k + top * 2);
    let dpr = Math.min(window.devicePixelRatio || 1, 2);
    dpr = Math.min(dpr, 30000 / cssH, Math.sqrt(16e6 / (cssW * cssH)));
    cv.width = Math.max(1, Math.round(cssW * dpr)); cv.height = Math.max(1, Math.round(cssH * dpr));
    cv.style.width = cssW + 'px'; cv.style.height = cssH + 'px';
    const x = cv.getContext('2d');
    x.setTransform(dpr, 0, 0, dpr, 0, 0);
    x.clearRect(0, 0, cssW, cssH);
    const X0 = G, Y0 = top, SW = SHEET_W * k, SH = len * k;
    x.fillStyle = x.createPattern(checkerTile(), 'repeat');
    x.fillRect(X0, Y0, SW, SH);
    /* a faint line at every foot */
    x.fillStyle = 'rgba(21,19,26,.10)';
    for (let i = 12; i < len - EPS; i += 12) x.fillRect(X0, Math.round(Y0 + i * k), SW, 1);
    /* room left at the end of the sheet */
    if (sh) {
      const from = END + sh.len, room = len - END - from;
      if (room > 0.2) {
        const y0 = Y0 + from * k, hh = room * k;
        x.fillStyle = 'rgba(128,44,201,.08)';
        x.fillRect(X0 + SIDE * k, y0, USABLE * k, hh);
        x.save(); x.beginPath(); x.rect(X0 + SIDE * k, y0, USABLE * k, hh); x.clip();
        x.strokeStyle = 'rgba(128,44,201,.18)'; x.lineWidth = 1;
        for (let d = -hh; d < USABLE * k; d += 10) { x.beginPath(); x.moveTo(X0 + SIDE * k + d, y0 + hh); x.lineTo(X0 + SIDE * k + d + hh, y0); x.stroke(); }
        x.restore();
        if (hh >= 22) {
          x.fillStyle = '#6A1FB0'; x.font = '700 12px Montserrat, Arial, sans-serif'; x.textAlign = 'center'; x.textBaseline = 'middle';
          x.fillText(f1(room) + ' in free', X0 + SW / 2, y0 + Math.min(hh / 2, 40));
        }
      }
      drawArt(x, sh, k, X0, Y0, true);
    } else {
      x.fillStyle = '#5E5A69'; x.font = '600 13px Montserrat, Arial, sans-serif'; x.textAlign = 'center'; x.textBaseline = 'middle';
      x.fillText('Your art shows up here', X0 + SW / 2, Y0 + SH / 2);
    }
    /* print area and sheet edge */
    x.setLineDash([4, 4]); x.strokeStyle = 'rgba(128,44,201,.4)'; x.lineWidth = 1;
    x.strokeRect(Math.round(X0 + SIDE * k) + 0.5, Math.round(Y0 + END * k) + 0.5, Math.round(USABLE * k) - 1, Math.round((len - 2 * END) * k) - 1);
    x.setLineDash([]); x.strokeStyle = 'rgba(21,19,26,.35)';
    x.strokeRect(X0 + 0.5, Y0 + 0.5, SW - 1, SH - 1);
    /* ruler: a tick every inch when there is room, a long one and a label every 12 in */
    x.fillStyle = '#5E5A69'; x.font = '600 10px Montserrat, Arial, sans-serif'; x.textAlign = 'right'; x.textBaseline = 'middle';
    const step = k >= 4 ? 1 : 6;
    for (let i = 0; i <= len + EPS; i += step) {
      const y = Math.round(Y0 + i * k), major = i % 12 === 0;
      const t = major ? 9 : i % 6 === 0 ? 6 : 3;
      x.fillRect(G - 2 - t, y, t, 1);
      if (major && i > 0) x.fillText(fin(i / 12) + ' ft', G - 13, y);
    }
    x.fillRect(G - 2, Y0, 1, SH);
  }
  function drawAll() {
    const P = S.pack, box = el.prev;
    const cssW = Math.max(200, Math.floor(box.clientWidth - 24));
    const sheets = P && P.sheets.length ? P.sheets : [null];
    let h = '';
    sheets.forEach((sh, i) => {
      const v = sh ? sh.variant : S.variants[0];
      let cap = '';
      if (sh && sheets.length > 1) cap = '<p class="gsb-cap"><b>Sheet ' + (i + 1) + ' of ' + sheets.length + '</b><span>' + esc(nice(v.title)) + (S.prod === 'ok' ? ' · ' + money(v.price) : '') + '</span></p>';
      let hint = '';
      if (sh) {
        const left = v.len - (sh.len + 2 * END);
        const idx = S.variants.indexOf(v), smaller = idx > 0 ? S.variants[idx - 1] : null;
        const over = smaller ? sh.len + 2 * END - smaller.len : 0;
        if (left >= 0.5) hint = 'You have ' + f1(left) + ' in left over. Add more art free.';
        else hint = 'This sheet is full.';
        if (smaller && over > 0 && over <= 2) hint += ' Your art runs ' + f1(over) + ' in past the ' + esc(shortName(smaller)) + ' sheet. Shrink a design a little to fit it' + (S.prod === 'ok' ? ' and save ' + money(v.price - smaller.price) : '') + '.';
        hint = '<p class="gsb-left">' + hint + '</p>';
        if (sh.placed.some(p => p.rot)) hint += '<p class="gsb-left is-soft">Some copies are turned sideways to use less film. They press the same.</p>';
      }
      h += '<figure class="gsb-sheet">' + cap + '<canvas role="img" aria-label="' + esc(sh ? 'Layout of sheet ' + (i + 1) + ', ' + nice(v.title) + ', with ' + sh.placed.length + ' pieces' : 'Empty ' + nice(v.title) + ' sheet') + '"></canvas>' + hint + '</figure>';
    });
    box.innerHTML = h;
    const cvs = box.querySelectorAll('canvas');
    sheets.forEach((sh, i) => drawSheet(cvs[i], sh, sh ? sh.variant.len : S.variants[0].len, cssW));
  }
  /* redraw when the preview gets wider or narrower (rotating a phone, resizing the window) */
  let lastW = 0, rt = 0;
  const refit = () => { const w = el.prev.clientWidth; if (w && Math.abs(w - lastW) > 2) { lastW = w; drawAll(); } };
  if (window.ResizeObserver) new ResizeObserver(refit).observe(el.prev);
  window.addEventListener('resize', () => { clearTimeout(rt); rt = setTimeout(refit, 150); });

  /* ---------- cart ---------- */
  /* every position, for our production team. It goes on the order as the hidden _Layout property, so the cart,
     checkout and emails show only the short Layout summary. */
  function layoutText(sh, i, total) {
    const groups = {};
    sh.placed.forEach(p => { (groups[p.pc.n] = groups[p.pc.n] || { it: p.pc.it, n: p.pc.n, list: [] }).list.push(p); });
    const lines = ['Sheet ' + (i + 1) + ' of ' + total + ', ' + sh.variant.title + '. Art runs ' + fin(sh.len) + ' in. Positions are x,y in inches from the top left corner of the sheet to the top left corner of each piece; R = turned 90 degrees.'];
    Object.keys(groups).map(Number).sort((a, b) => a - b).forEach(n => {
      const g = groups[n], it = g.it, c = g.list.length, rots = g.list.filter(p => p.rot).length;
      const pos = g.list.map(p => fin(SIDE + p.x) + ',' + fin(END + p.y) + (p.rot ? ' R' : '')).join('; ');
      lines.push('Image ' + n + ' (' + it.name + (it.knocked ? ', white background removed' : '') + '): ' + fin(it.w) + ' x ' + fin(heightOf(it)) + ' in, ' + c + (c === 1 ? ' copy' : ' copies') +
        ', rotated ' + (!rots ? 'no' : rots === c ? 'yes' : rots + ' of ' + c) + '. At ' + pos + '.');
    });
    return lines.join('\n');
  }
  function sheetPNG(sh) {
    const len = sh.variant.len, k = Math.min(40, 4000 / len);
    const c = document.createElement('canvas');
    c.width = Math.round(SHEET_W * k); c.height = Math.round(len * k);
    const x = c.getContext('2d');
    x.strokeStyle = 'rgba(0,0,0,.3)'; x.lineWidth = 1;
    x.strokeRect(0.5, 0.5, c.width - 1, c.height - 1);
    drawArt(x, sh, k, 0, 0, false);
    return new Promise(res => { try { c.toBlob(b => res(b), 'image/png'); } catch (e) { res(null); } });
  }
  /* the designs on a sheet, by image number */
  function usedOf(sh) {
    const used = {};
    sh.placed.forEach(p => { used[p.pc.n] = p.pc.it; });
    return Object.keys(used).map(Number).sort((a, b) => a - b).map(n => ({ n: n, it: used[n] }));
  }
  /* 53-bit string hash (cyrb53) */
  function hash(s) {
    let h1 = 0xdeadbeef, h2 = 0x41c6ce57;
    for (let i = 0; i < s.length; i++) { const c = s.charCodeAt(i); h1 = Math.imul(h1 ^ c, 2654435761); h2 = Math.imul(h2 ^ c, 1597334677); }
    h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
    h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
    return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(36);
  }
  /* what one sheet's cart line carries, plus a fingerprint of it: the sheet size and number, every position and each
     file (name, size, date, white removed or not). Re-arranging back to the same layout, or rebuilding it from the
     same files after a reload, gives the same fingerprint. */
  function sheetInfo(sh, i, total) {
    if (sh.info && sh.info.i === i && sh.info.total === total) return sh.info;
    const used = usedOf(sh), n = sh.placed.length, layout = layoutText(sh, i, total);
    const summary = (total > 1 ? 'Sheet ' + (i + 1) + ' of ' + total + ': ' : '') + used.length + (used.length === 1 ? ' design, ' : ' designs, ') +
      n + (n === 1 ? ' piece' : ' pieces') + ' on ' + SHEET_W + ' x ' + fin(sh.variant.len) + ' in';
    const files = used.map(u => u.n + '=' + [u.it.file.name, u.it.file.size, u.it.file.lastModified, u.it.knocked ? 1 : 0].join('/'));
    sh.info = { i: i, total: total, used: used, layout: layout, summary: summary, hash: hash([sh.variant.id, summary, layout].concat(files).join('\n')) };
    return sh.info;
  }

  /* Sheets this builder has put in the cart (S.inCart). If an add stops partway, a retry adds only the sheets that
     are missing, and a changed layout asks before anything else goes in. Until the whole layout is in, the list is
     also kept in sessionStorage, so "reload and try again" can't double a sheet either. A sheet only counts while
     its line is still in the cart. */
  const REC = 'gfs:gsbCart';
  let persist = true;
  function loadRecs() {
    try { const a = JSON.parse(sessionStorage.getItem(REC) || '[]'); return Array.isArray(a) ? a.filter(r => r && typeof r.hash === 'string') : []; }
    catch (e) { return []; }
  }
  function saveRecs() {
    try { if (persist && S.inCart.length) sessionStorage.setItem(REC, JSON.stringify(S.inCart)); else sessionStorage.removeItem(REC); }
    catch (e) { /* storage blocked: the list in memory still works */ }
  }
  async function cartKeys() {
    try {
      const r = await fetch(ROOT + 'cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin', cache: 'no-store' });
      if (!r.ok) return null;
      const c = await r.json();
      return ((c && c.items) || []).map(x => x.key);
    } catch (e) { return null; }
  }
  /* forget sheets that are gone from the cart. If the cart can't be read, keep them all, so nothing goes in twice. */
  async function syncRecs() {
    if (!S.inCart.length) return;
    const keys = await cartKeys();
    if (!keys) return;
    const before = S.inCart.length;
    S.inCart = S.inCart.filter(r => !r.key || keys.indexOf(r.key) >= 0);
    if (S.inCart.length !== before) saveRecs();
  }
  async function dropLines(recs) {
    for (const r of recs) {
      let ok = false;
      if (r.key) {
        try {
          const res = await fetch(ROOT + 'cart/change.js', { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ id: r.key, quantity: 0 }) });
          ok = res.ok;
        } catch (e) { ok = false; }
        if (!ok) { const keys = await cartKeys(); ok = !!keys && keys.indexOf(r.key) < 0; }
      }
      if (!ok) throw new Error('We couldn’t take the earlier sheets out of your cart. Remove them in your cart, then try again.');
      S.inCart = S.inCart.filter(x => x !== r);
      saveRecs();
    }
  }
  /* for the current layout: which sheets still need adding, and which sheets in the cart aren't part of it */
  function plan(P) {
    const total = P.sheets.length, info = P.sheets.map((sh, i) => sheetInfo(sh, i, total));
    const stale = S.inCart.filter(r => !info.some(x => x.hash === r.hash));
    const todo = info.filter(x => !S.inCart.some(r => r.hash === x.hash));
    let cost = 0;
    todo.forEach(x => { cost += P.sheets[x.i].variant.price || 0; });
    return { info: info, stale: stale, todo: todo, cost: cost };
  }
  function ask(n) {
    const one = n === 1;
    setNote('<div class="gsb-ask"><p id="gsb-ask-t"><b>Your cart already has ' + (one ? '1 sheet' : n + ' sheets') + ' from an earlier layout.</b> Replace ' + (one ? 'it' : 'them') + ' with this layout, or check your cart first.</p>' +
      '<div class="gsb-row"><button type="button" class="btn btn-violet" data-gsb-replace aria-describedby="gsb-ask-t">Replace with this layout</button><a class="btn btn-line" href="' + ROOT + 'cart">View cart</a></div>' +
      '<button type="button" class="gsb-link" data-gsb-keep>Keep ' + (one ? 'it' : 'them') + ' and add this layout too</button></div>');
    const b = el.note.querySelector('[data-gsb-replace]');
    if (b) b.focus();
  }
  function post(url, fd, onUp) {
    return new Promise(resolve => {
      const x = new XMLHttpRequest();
      x.open('POST', url);
      x.setRequestHeader('Accept', 'application/json');
      x.timeout = 300000;
      if (x.upload) x.upload.onprogress = e => { if (e.lengthComputable) onUp(e.loaded / e.total); };
      x.onload = () => { let j = null; try { j = JSON.parse(x.responseText); } catch (e) { j = null; } resolve({ ok: x.status >= 200 && x.status < 300, status: x.status, j: j }); };
      x.onerror = () => resolve({ ok: false, status: 0, j: null });
      x.ontimeout = () => resolve({ ok: false, status: -1, j: null });
      x.send(fd);
    });
  }
  function errText(r) {
    if (r.status === 0) return 'The upload didn’t go through. Check your connection and try again.';
    if (r.status === -1) return 'The upload took too long. Try again on a faster connection, or use smaller files.';
    if (r.status === 413) return 'This sheet’s files are too large to upload together. Use smaller files, or email them to ' + EMAIL + ' and we’ll build your sheet.';
    if (r.status === 429) return 'Too many tries at once. Wait a minute, then try again.';
    const j = r.j || {}, d = j.description || j.message;
    return (typeof d === 'string' && d ? d.replace(/<[^>]+>/g, '') : 'The cart didn’t accept this sheet.') + (/[.!?]$/.test(String(d || '')) ? '' : '.');
  }
  function progress(text, f) {
    el.prog.hidden = false;
    el.prog.querySelector('span').textContent = text;
    el.prog.querySelector('i').style.width = Math.round(Math.max(0.03, Math.min(1, f)) * 100) + '%';
  }
  function lock(on) {
    S.adding = on;
    app.classList.toggle('is-adding', on);
    el.file.disabled = on;
    if (el.work) el.work.setAttribute('aria-busy', on ? 'true' : 'false');
  }
  /* mode: undefined (the Add button), 'replace' or 'keep' (the answers when the cart holds sheets of another layout) */
  async function addToCart(mode) {
    flush();
    const P = S.pack;
    if (S.adding || S.prod !== 'ok' || !P || !P.sheets.length || P.rest.length || P.tooMany) return;
    if (S.items.some(it => it.loading || it.busy)) return say('One moment. Your files are still opening.', 'warn');
    if (heavyAt(P) >= 0) return renderFoot();
    const total = P.sheets.length;
    let pl = plan(P);
    /* this exact layout is already in the cart (after coming back to this page, say) */
    if (S.inCart.length && !pl.stale.length && !pl.todo.length) { window.location.href = ROOT + 'cart'; return; }
    lock(true);
    el.add.disabled = true;
    el.add.textContent = 'Adding to cart…';
    say('');
    setNote('');
    let i = -1, step = 'check';
    try {
      if (S.inCart.length) {
        progress('Checking your cart…', 0);
        await syncRecs();
        pl = plan(P);
      }
      if (pl.stale.length) {
        if (!mode) {
          lock(false);
          el.prog.hidden = true;
          renderFoot();
          ask(pl.stale.length);
          return;
        }
        persist = true;
        if (mode === 'replace') {
          step = 'remove';
          progress('Taking the earlier sheets out of your cart…', 0);
          await dropLines(pl.stale);
        } else {
          S.inCart = S.inCart.filter(r => pl.stale.indexOf(r) < 0);
          saveRecs();
        }
        pl = plan(P);
      }
      step = 'add';
      persist = true;
      for (const x of pl.todo) {
        i = x.i;
        const sh = P.sheets[i], pre = total > 1 ? 'Sheet ' + (i + 1) + ' of ' + total + ': ' : '';
        progress(pre + 'getting your files ready…', 0);
        const fd = new FormData();
        fd.append('id', String(sh.variant.id));
        fd.append('quantity', '1');
        fd.append('properties[Sheet]', sh.variant.title);
        fd.append('properties[Layout]', x.summary);
        fd.append('properties[_Layout]', x.layout);
        let bytes = 0;
        x.used.forEach(u => {
          const it = u.it, ext = (/\.[a-z0-9]+$/i.exec(it.work.name || '') || ['.png'])[0].toLowerCase();
          const nm = 'image-' + u.n + '-' + (it.work.name || 'art').replace(/\.[a-z0-9]+$/i, '').replace(/[^\w-]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60) + ext;
          fd.append('properties[Image ' + u.n + ']', it.work, nm);
          bytes += it.work.size;
        });
        const png = await sheetPNG(sh);
        if (png) fd.append('properties[_Preview]', png, 'gang-sheet-' + (i + 1) + '-preview.png');
        const mb = bytes / MB;
        const r = await post(ROOT + 'cart/add.js', fd, f => progress(pre + 'uploading ' + (mb >= 1 ? f1(mb) + ' MB' : 'your files') + '… ' + Math.round(f * 100) + '%', f));
        if (!r.ok) throw new Error(errText(r));
        const j = r.j || {}, line = j.key ? j : (j.items && j.items[0]) || {};
        S.inCart.push({ hash: x.hash, key: line.key || '', title: sh.variant.title });
        saveRecs();
      }
      /* the whole layout is in. Remember that on this page (going back to it and pressing Add won't double it),
         but not after a reload. */
      persist = false;
      saveRecs();
      progress('Added. Opening your cart…', 1);
      window.location.href = ROOT + 'cart';
    } catch (e) {
      lock(false);
      el.prog.hidden = true;
      renderFoot();
      const why = esc(e && e.message ? e.message : 'Something went wrong.');
      const done = step === 'add' ? total - plan(P).todo.length : 0;
      if (step !== 'add') say(why, 'error');
      else say((total > 1 ? 'Sheet ' + (i + 1) + ' of ' + total + ' wasn’t added. ' : 'Your sheet wasn’t added. ') + why +
        (done ? ' ' + done + (done === 1 ? ' sheet is' : ' sheets are') + ' already in your <a href="' + ROOT + 'cart">cart</a>. Try again to add the rest. We won’t add ' + (done === 1 ? 'that one' : 'those') + ' twice.' : ' Try again, or call ' + esc(PHONE) + ' and we’ll take the order by phone.'), 'error');
      el.msg.scrollIntoView({ block: 'nearest' });
    }
  }
  el.add.addEventListener('click', () => addToCart());
  el.note.addEventListener('click', e => {
    if (e.target.closest('[data-gsb-replace]')) addToCart('replace');
    else if (e.target.closest('[data-gsb-keep]')) addToCart('keep');
  });
  /* back to this page from the cart: unlock the builder and recheck what's in the cart */
  window.addEventListener('pageshow', e => {
    if (!e.persisted) return;
    if (S.adding) { lock(false); el.prog.hidden = true; }
    syncRecs().then(renderFoot);
  });

  app.gsb = { state: S, layout: layoutText, arrange: arrange };
  S.inCart = loadRecs();
  /* the DTF / UV DTF switch keeps the art and layout and reloads sheet prices from the other product */
  function syncType() {
    const box = $('[data-gsb-type]'), kind = $('[data-gsb-kind]');
    if (box) box.querySelectorAll('[data-type]').forEach(b => b.setAttribute('aria-checked', String(b.dataset.type === TYPE)));
    if (kind) kind.textContent = TYPE === 'uv' ? 'UV DTF' : 'DTF';
    app.classList.toggle('is-uv', TYPE === 'uv');
  }
  const typeBox = $('[data-gsb-type]');
  if (typeBox) typeBox.addEventListener('click', e => {
    const b = e.target.closest('[data-type]');
    if (!b || b.dataset.type === TYPE || S.adding) return;
    TYPE = b.dataset.type === 'uv' ? 'uv' : 'dtf';
    CFG.handle = TYPE === 'uv' ? CFG.uvHandle : DTF_HANDLE;
    try { const u = new URL(location.href); if (TYPE === 'uv') u.searchParams.set('type', 'uv'); else u.searchParams.delete('type'); history.replaceState(null, '', u); } catch (err) {}
    S.prod = 'loading'; S.variants = fallbackVariants(); S.inCart = [];
    syncType(); renderStatus(); loadProduct();
  });
  syncType();
  renderList(); renderStatus(); drawAll(); loadProduct();
  if (S.inCart.length) syncRecs().then(() => {
    const n = S.inCart.length;
    if (n) say((n === 1 ? '1 sheet' : n + ' sheets') + ' from your last try ' + (n === 1 ? 'is' : 'are') + ' already in your <a href="' + ROOT + 'cart">cart</a>. Add the same art at the same sizes and we’ll add only what’s missing.');
    renderFoot();
  });
})();